let debounceTimer = null

/**
 * 通用分页列表模块工厂函数
 * @param {Function} requestFn - 请求函数（必须返回 Promise，返回结构包含列表和总数）
 * @param {Object} options - 配置项（字段名、默认分页、是否防抖等）
 */
export function createListModule(requestFn, options = {}) {
  const {
    pageKey = 'page',           // 请求参数中表示“当前页”的字段名
    pageSizeKey = 'size',   // 请求参数中表示“每页条数”的字段名
    listKey = 'result',        // 响应中表示“列表数组”的字段名
    totalKey = 'total',         // 响应中表示“总条数”的字段名
    initialPage = 1,            // 初始页码
    initialPageSize = 10,       // 初始每页条数
    debounceDelay = 300,        // 防抖间隔（单位：ms）
    enableDebounce = false   ,   // 是否启用请求防抖
    extraMutations = {}, // ✅ 这里新增支持扩展 mutation
    extraGetters = {} // ✅ 这里新增支持扩展 mutation
  } = options

  return {
    namespaced: true, // 启用命名空间，支持模块化调用

    state: () => ({
      list: [],                    // 当前页列表数据
      total: 0,                    // 总条数
      [pageKey]: initialPage,      // 当前页码（字段名可配置）
      [pageSizeKey]: initialPageSize, // 每页条数（字段名可配置）
      loading: false,              // 加载中状态
      query: {},                   // 当前请求的查询参数（给组件使用）
      _lastQuery: {}               // 缓存的上一次查询参数（翻页、改条数用）
    }),
    getters:{
      ...extraGetters
    },
    mutations: {
      SET_LIST(state, list) {
        state.list = list
      },
      SET_TOTAL(state, total) {
        state.total = total
      },
      SET_PAGE(state, page) {
        state[pageKey] = page
      },
      SET_PAGE_SIZE(state, size) {
        state[pageSizeKey] = size
      },
      SET_LOADING(state, bool) {
        state.loading = bool
      },
      SET_QUERY(state, query) {
        // 同时更新对外可见的 query 和内部缓存 _lastQuery
        state.query = { ...query }
        state._lastQuery = { ...query }
      },
      ...extraMutations // ✅ 合并额外的 mutations
    },

    actions: {
      /**
       * 获取列表数据（支持防抖、分页、参数缓存）
       * @param {Object} payload - 查询参数对象
       */
      getList({ commit, state }, payload = {}) {
        const runRequest = async () => {
          commit('SET_LOADING', true)
          commit('SET_QUERY', payload)

          try {
            const params = {
              [pageKey]: state[pageKey],
              [pageSizeKey]: state[pageSizeKey],
              ...payload
            }

            const res = await requestFn(params)
            const data = res.data || {}

            // 判断接口是否返回了分页数据，若没有返回分页，则直接更新列表数据
            if (data[listKey] && data[totalKey] !== undefined) {
              // 分页数据
              commit('SET_LIST', data[listKey] || [])
              commit('SET_TOTAL', data[totalKey] || 0)
            } else {
              // 非分页数据，直接更新 list
              commit('SET_LIST', Array.isArray(data) ? data : [])
              commit('SET_TOTAL', 0) // 对于非分页接口，不需要 total
            }
          } catch (e) {
            console.error('[getList 请求失败]', e)
          } finally {
            commit('SET_LOADING', false)
          }
        }

        if (enableDebounce) {
          // 防抖处理，避免短时间内重复请求
          if (debounceTimer) clearTimeout(debounceTimer)
          debounceTimer = setTimeout(() => {
            runRequest()
          }, debounceDelay)
        } else {
          runRequest()
        }
      },

      /**
       * 翻页（保留上次的查询参数）
       */
      changePage({ commit, dispatch, state }, page) {
        commit('SET_PAGE', page)
        dispatch('getList', state._lastQuery)
      },

      /**
       * 修改每页条数（自动重置到第一页，保留查询参数）
       */
      changePageSize({ commit, dispatch, state }, size) {
        commit('SET_PAGE_SIZE', size)
        commit('SET_PAGE', initialPage)
        dispatch('getList', state._lastQuery)
      }
    }
  }
}
